import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, ShieldCheck, Heart, Wand2, Gift } from "lucide-react";
import CreateYourOwn from "../components/CreateYourOwn";
import EmailGate from "../components/EmailGate";
import RecentBuddiesMarquee from "../components/RecentBuddiesMarquee";
import UrgencyBanner from "../components/UrgencyBanner";
import { trackEvent, trackOnce } from "../lib/analytics";
import useSeo from "../lib/useSeo";

const FUNNEL_SOURCE = "free_buddy_funnel";

/**
 * Plate Buddies funnel landing page — `/free-buddy`
 *
 * Single-goal page: free Buddy → email capture → cart upsell.
 * Intentionally minimal chrome (no nav, no footer links) so the only path forward
 * is "make my buddy" → reveal → cart. Used as the destination for paid ads and
 * Instagram-bio link traffic.
 */
export default function FreeBuddy() {
  useSeo({
    title: "Design a free Plate Buddy — your kid's very own food character",
    description:
      "Free character-led kids' meal generator. Design your child's own Plate Buddy — a friendly food character paired with a kid-friendly recipe — in 30 seconds. No credit card required.",
    url: "https://platebuddies.com/free-buddy",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    // Fire once per page-mount so reloads don't multiply landings.
    trackOnce("funnel_view", { funnel: FUNNEL_SOURCE });
  }, []);
  // Pop the upsell banner once the user scrolls past the generator (i.e. after
  // they've engaged + likely seen their buddy). Cheap heuristic that beats
  // trying to wire into <CreateYourOwn />'s internal `result` state.
  const [showUpsell, setShowUpsell] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 1200 && !showUpsell) setShowUpsell(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showUpsell]);

  return (
    <div data-testid="page-free-buddy" className="font-body bg-pb-cream min-h-screen">
      <UrgencyBanner />
      <MinimalHeader />
      <HeroHook />
      <SocialProofStrip />
      <RecentBuddiesMarquee
        theme="cream"
        limit={24}
        headline="Look what other families just made"
        subline="A live look at Buddies real kids dreamed up this week — your child's could be next."
      />

      {/* The actual conversion engine — gated by email, then the generator does the magic. */}
      <section id="make-buddy" className="relative scroll-mt-24">
        <EmailGate
          feature="generator"
          title="Tap to design your child's Plate Buddy"
          blurb="Free for early-access families. Name + one sentence is all it takes."
          source="free_buddy_funnel"
        >
          <CreateYourOwn />
        </EmailGate>
      </section>

      <UpsellBanner />
      <FoundersNote />
      <FinalCta />
      <MicroFooter />

      {/* Sticky bottom CTA that appears after engagement */}
      {showUpsell && <StickyUpsell />}
    </div>
  );
}

function MinimalHeader() {
  return (
    <header className="bg-pb-cream/90 backdrop-blur sticky top-0 z-40 border-b border-pb-sky/20">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/home" data-testid="funnel-logo" className="font-display font-black text-xl text-pb-ink hover:text-pb-coral transition">
          Plate<span className="text-pb-coral">Buddies</span>
        </Link>
        <a
          href="#make-buddy"
          data-testid="funnel-header-cta"
          className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-pb-ink text-white text-xs font-extrabold px-4 py-2 hover:bg-pb-coral transition"
        >
          <Wand2 className="h-3.5 w-3.5" /> Make a free Buddy
        </a>
      </div>
    </header>
  );
}

function HeroHook() {
  return (
    <section className="relative overflow-hidden">
      <div className="blob bg-pb-sun" style={{ width: 420, height: 420, top: -120, left: -100, opacity: 0.45 }} />
      <div className="blob bg-pb-coral" style={{ width: 360, height: 360, bottom: -100, right: -100, opacity: 0.25 }} />

      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20 pb-12 text-center">
        <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-pb-coral border border-pb-coral/20 shadow-sm">
          <Sparkles className="h-3.5 w-3.5" /> Free · 30 seconds · Ages 2–7
        </span>
        <h1 data-testid="hero-headline" className="font-display font-black tracking-tight text-pb-ink mt-5 text-4xl sm:text-6xl md:text-7xl leading-[0.95]">
          Design a <span className="wavy">Plate Buddy</span><br />
          your kid will love.
        </h1>
        <p data-testid="hero-sub" className="mt-6 max-w-2xl mx-auto text-base sm:text-xl text-pb-soft leading-relaxed">
          We illustrate your child's one-of-a-kind food character — paired with a kid-friendly recipe — in about 30 seconds.
          <span className="block mt-1 font-bold text-pb-ink">Mealtime as a story instead of a battle.</span>
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="#make-buddy"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-7 py-4 text-base shadow-[0_14px_32px_rgba(242,106,52,0.45)] hover:scale-105 transition"
          >
            <Wand2 className="h-5 w-5" /> Make my Buddy — Free
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <p className="text-xs text-pb-soft font-bold inline-flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-pb-aqua" /> No credit card · Email unlock
          </p>
        </div>

        {/* Visual proof — a tiny lineup of the official buddies */}
        <div className="mt-12 flex items-end justify-center gap-3 flex-wrap">
          {[
            { name: "Pineapple Chicky", src: "/buddies/pineapple-chicky.jpg", rotate: "-rotate-6" },
            { name: "Sally Salad", src: "/buddies/sally-salad.png", rotate: "rotate-3" },
            { name: "Captain Sammy", src: "/buddies/captain-sammy.png", rotate: "-rotate-2" },
            { name: "Maestro Meatball", src: "/buddies/maestro-meatball.jpg", rotate: "rotate-6" },
          ].map((b, i) => (
            <div
              key={b.name}
              className={`relative bg-white rounded-2xl shadow-[0_10px_24px_rgba(0,0,0,0.15)] border-4 border-white p-1.5 w-20 sm:w-24 ${b.rotate} animate-float-slow`}
              style={{ animationDelay: `${i * 0.3}s` }}
            >
              <img src={b.src} alt={b.name} className="w-full aspect-square object-cover rounded-xl" />
            </div>
          ))}
          <div className="rounded-2xl border-4 border-dashed border-pb-coral w-20 sm:w-24 aspect-square flex flex-col items-center justify-center rotate-3 bg-pb-cream">
            <Sparkles className="h-6 w-6 text-pb-coral" />
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-pb-coral mt-1">Yours?</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialProofStrip() {
  return (
    <section className="bg-white border-y border-pb-sky/30">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-center">
        <Stat label="founding families" value="15" />
        <span className="hidden sm:inline text-pb-sky">·</span>
        <Stat label="kid-tested recipes" value="6" />
        <span className="hidden sm:inline text-pb-sky">·</span>
        <Stat label="screen-free dinners" value="∞" />
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-display font-black text-2xl sm:text-3xl text-pb-coral">{value}</span>
      <span className="text-xs sm:text-sm font-bold text-pb-soft uppercase tracking-wider">{label}</span>
    </div>
  );
}

function UpsellBanner() {
  return (
    <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
      <div
        data-testid="upsell-banner"
        className="rounded-[2.5rem] p-8 sm:p-12 text-white relative overflow-hidden text-center"
        style={{
          background: "linear-gradient(135deg, #5B3F94 0%, #4E2C80 50%, #06A5A1 100%)",
        }}
      >
        <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-pb-sun/25 blur-3xl" />
        <div className="absolute -left-16 top-1/3 h-48 w-48 rounded-full bg-pb-coral/20 blur-3xl" />
        <div className="relative z-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-white border border-white/30">
            <Gift className="h-3.5 w-3.5" /> First-week founders bonus
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl mt-4 leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.25)]">
            Love your Buddy? Make it real.
          </h2>
          <p className="mt-3 text-white/90 text-base sm:text-lg max-w-xl mx-auto">
            Order the Starter Kit — character cards, play mat, weekly meal-plan magnets — and get <b className="text-pb-sun">10% off with code WELCOME10</b>.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/products/starter-kit"
              data-testid="upsell-cta"
              className="inline-flex items-center gap-2 rounded-full bg-pb-sun text-pb-ink font-extrabold px-7 py-4 hover:scale-105 transition shadow-[0_12px_28px_rgba(0,0,0,0.28)]"
            >
              Get the Starter Kit — $19.99
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/library"
              data-testid="upsell-library"
              className="text-sm font-bold text-white/80 hover:text-white underline-offset-4 hover:underline transition"
            >
              See your saved Buddies →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FoundersNote() {
  return (
    <section className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="rounded-[2rem] bg-white p-8 sm:p-10 border border-pb-sky/30 shadow-sm">
        <div className="flex items-center gap-3 text-pb-coral">
          <Heart className="h-5 w-5 fill-pb-coral" />
          <span className="text-xs font-extrabold uppercase tracking-widest">From the founder</span>
        </div>
        <p className="mt-4 font-display font-black text-xl sm:text-2xl leading-tight text-pb-ink">
          "I built Plate Buddies because dinner felt like a daily negotiation in our house."
        </p>
        <p className="mt-3 text-pb-soft leading-relaxed">
          The buddies turn the plate into a story, not a battlefield. My kids ask for "Sally Salad night" the way other kids ask for ice cream. If it works for us, I think it might work for you too.
        </p>
        <p className="mt-4 text-sm font-bold text-pb-ink">— Michelle, mom of three · founder</p>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12 text-center">
      <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight">
        Ready to turn dinner into a story?
      </h2>
      <p className="mt-3 text-pb-soft">
        Free, fast, and your kid will giggle. (Yours might too.)
      </p>
      <a
        href="#make-buddy"
        data-testid="final-cta"
        onClick={() => trackEvent("funnel_cta_click", { funnel: FUNNEL_SOURCE, position: "final" })}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-8 py-4 shadow-[0_14px_32px_rgba(242,106,52,0.45)] hover:scale-105 transition"
      >
        <Wand2 className="h-5 w-5" /> Make my Buddy
        <ArrowRight className="h-4 w-4" />
      </a>
    </section>
  );
}

function MicroFooter() {
  return (
    <footer className="bg-pb-ink text-white/70 mt-8">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
        <p className="font-bold">© Plate Buddies™ · Built by a mom for every family</p>
        <div className="flex gap-5">
          <Link to="/home" className="hover:text-pb-sun transition" data-testid="footer-home">Home</Link>
          <Link to="/shop" className="hover:text-pb-sun transition" data-testid="footer-shop">Shop</Link>
          <Link to="/privacy" className="hover:text-pb-sun transition">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}

function StickyUpsell() {
  return (
    <div
      data-testid="sticky-upsell"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white rounded-2xl shadow-[0_18px_44px_rgba(0,0,0,0.25)] px-4 py-3 flex items-center gap-3 border-2 border-pb-coral animate-fade-up no-print max-w-[95%]"
    >
      <Sparkles className="h-5 w-5 text-pb-coral flex-shrink-0" />
      <span className="text-sm font-bold text-pb-ink hidden sm:block">Love it? Make it real with the Starter Kit.</span>
      <Link
        to="/products/starter-kit"
        onClick={() => trackEvent("funnel_upsell_click", { funnel: FUNNEL_SOURCE, position: "sticky", coupon: "WELCOME10" })}
        className="rounded-full bg-pb-coral text-white text-xs font-extrabold px-4 py-2 hover:scale-105 transition whitespace-nowrap"
        data-testid="sticky-upsell-cta"
      >
        Get kit — 10% off
      </Link>
    </div>
  );
}
